import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { AppFeature } from '@app/core/models/config.models';
import { ShortcutAction } from '@app/core/models/keyboard-shortcut.models';
import { AppConfigService } from '@app/core/services/app-config.service';
import { AppTranslateService } from '@app/core/services/app-translate.service';
import { KeyboardShortcutService } from '@app/core/services/keyboard-shortcut.service';
import { ClearableInputComponent } from '@app/shared/components/clearable-input/clearable-input.component';
import { TranslateModule } from '@ngx-translate/core';

import { SettingsPage } from '../../models/settings-page';

interface ShortcutListItem {
  labelKey: string;
  action: ShortcutAction;
  /** Set on entries that are unreachable when calling is disabled by account settings. */
  requiresCalling?: boolean;
}

interface ShortcutSection {
  titleKey: string;
  shortcuts: ShortcutListItem[];
  /** Set on sections that are unreachable when calling is disabled by account settings. */
  requiresCalling?: boolean;
}

const ALL_SECTIONS: ShortcutSection[] = [
  {
    titleKey: 'KEYBOARD_SHORTCUTS_PREFERENCES.CALL_CONTROL_TITLE',
    requiresCalling: true,
    shortcuts: [
      { labelKey: 'KEYBOARD_SHORTCUTS_PREFERENCES.ANSWER_INCOMING_CALL', action: ShortcutAction.AnswerCall },
      { labelKey: 'KEYBOARD_SHORTCUTS_PREFERENCES.HANG_UP_END_CALL', action: ShortcutAction.HangUp },
      { labelKey: 'KEYBOARD_SHORTCUTS_PREFERENCES.MUTE_UNMUTE', action: ShortcutAction.ToggleMute },
      { labelKey: 'KEYBOARD_SHORTCUTS_PREFERENCES.HOLD_RESUME', action: ShortcutAction.ToggleHold },
      { labelKey: 'KEYBOARD_SHORTCUTS_PREFERENCES.ATTENDED_TRANSFER', action: ShortcutAction.AttendedTransfer },
      { labelKey: 'KEYBOARD_SHORTCUTS_PREFERENCES.PARK_RETRIEVE_PARK', action: ShortcutAction.ParkCall },
      { labelKey: 'KEYBOARD_SHORTCUTS_PREFERENCES.TOGGLE_CALL_RECORDING', action: ShortcutAction.ToggleRecording },
      { labelKey: 'KEYBOARD_SHORTCUTS_PREFERENCES.MERGE_CONFERENCE', action: ShortcutAction.MergeCalls },
      { labelKey: 'KEYBOARD_SHORTCUTS_PREFERENCES.OPEN_DIALPAD', action: ShortcutAction.OpenDialpad },
    ],
  },
  {
    titleKey: 'KEYBOARD_SHORTCUTS_PREFERENCES.NAVIGATION_TITLE',
    shortcuts: [
      {
        labelKey: 'KEYBOARD_SHORTCUTS_PREFERENCES.SWITCH_TO_CALLS',
        action: ShortcutAction.SwitchToCalls,
        requiresCalling: true,
      },
      { labelKey: 'KEYBOARD_SHORTCUTS_PREFERENCES.SWITCH_TO_MESSAGES', action: ShortcutAction.SwitchToMessages },
      {
        labelKey: 'KEYBOARD_SHORTCUTS_PREFERENCES.SWITCH_TO_VOICEMAIL',
        action: ShortcutAction.SwitchToVoicemail,
        requiresCalling: true,
      },
      { labelKey: 'KEYBOARD_SHORTCUTS_PREFERENCES.OPEN_FOCUS_SEARCH', action: ShortcutAction.FocusSearch },
      {
        labelKey: 'KEYBOARD_SHORTCUTS_PREFERENCES.JUMP_TO_ACTIVE_CALL',
        action: ShortcutAction.JumpToActiveCall,
        requiresCalling: true,
      },
    ],
  },
  {
    titleKey: 'KEYBOARD_SHORTCUTS_PREFERENCES.PRESENCE_TITLE',
    shortcuts: [{ labelKey: 'KEYBOARD_SHORTCUTS_PREFERENCES.TOGGLE_DND', action: ShortcutAction.ToggleDnd }],
  },
  {
    titleKey: 'KEYBOARD_SHORTCUTS_PREFERENCES.MESSAGING_SMS_TITLE',
    shortcuts: [
      { labelKey: 'KEYBOARD_SHORTCUTS_PREFERENCES.NEW_MESSAGE_COMPOSE', action: ShortcutAction.NewMessage },
      {
        labelKey: 'KEYBOARD_SHORTCUTS_PREFERENCES.REPLY_TO_LAST_CONVERSATION',
        action: ShortcutAction.ReplyToLastConversation,
      },
      {
        labelKey: 'KEYBOARD_SHORTCUTS_PREFERENCES.MARK_CONVERSATION_READ',
        action: ShortcutAction.MarkConversationRead,
      },
    ],
  },
  {
    titleKey: 'KEYBOARD_SHORTCUTS_PREFERENCES.VOICEMAIL_TITLE',
    requiresCalling: true,
    shortcuts: [
      { labelKey: 'KEYBOARD_SHORTCUTS_PREFERENCES.VOICEMAIL_PLAY_PAUSE', action: ShortcutAction.VoicemailPlayPause },
      {
        labelKey: 'KEYBOARD_SHORTCUTS_PREFERENCES.VOICEMAIL_SKIP_FORWARD',
        action: ShortcutAction.VoicemailSkipForward,
      },
      { labelKey: 'KEYBOARD_SHORTCUTS_PREFERENCES.VOICEMAIL_SKIP_BACK', action: ShortcutAction.VoicemailSkipBack },
      { labelKey: 'KEYBOARD_SHORTCUTS_PREFERENCES.VOICEMAIL_DELETE', action: ShortcutAction.VoicemailDelete },
      { labelKey: 'KEYBOARD_SHORTCUTS_PREFERENCES.VOICEMAIL_CALL_BACK', action: ShortcutAction.VoicemailCallBack },
    ],
  },
  {
    titleKey: 'KEYBOARD_SHORTCUTS_PREFERENCES.DIRECTORY_CONTACTS_TITLE',
    shortcuts: [
      {
        labelKey: 'KEYBOARD_SHORTCUTS_PREFERENCES.OPEN_CONTACTS_DIRECTORY',
        action: ShortcutAction.SwitchToContacts,
      },
      {
        labelKey: 'KEYBOARD_SHORTCUTS_PREFERENCES.INITIATE_CALL_SELECTED_CONTACT',
        action: ShortcutAction.CallSelectedContact,
        requiresCalling: true,
      },
    ],
  },
];

@Component({
  standalone: true,
  imports: [CommonModule, TranslateModule, ClearableInputComponent],
  selector: 'app-keyboard-shortcuts',
  templateUrl: './keyboard-shortcuts.component.html',
  styleUrls: ['./keyboard-shortcuts.component.scss'],
})
export class KeyboardShortcutsComponent extends SettingsPage {
  protected readonly sections: ShortcutSection[];

  protected search = '';
  protected filteredSections: ShortcutSection[];

  constructor(
    private keyboardShortcutService: KeyboardShortcutService,
    private appTranslateService: AppTranslateService,
    private appConfigService: AppConfigService
  ) {
    super();

    const callingEnabled = this.appConfigService.isFeatureEnabled(AppFeature.EnableCalling);
    this.sections = callingEnabled
      ? ALL_SECTIONS
      : ALL_SECTIONS.filter((section) => !section.requiresCalling).map((section) => ({
          ...section,
          shortcuts: section.shortcuts.filter((shortcut) => !shortcut.requiresCalling),
        }));
    this.filteredSections = this.sections;
  }

  protected onSearchChange(term: string): void {
    this.search = term;
    this.filteredSections = this.filterSections(term);
  }

  private filterSections(term: string): ShortcutSection[] {
    const normalizedTerm = term.trim().toLowerCase();
    if (!normalizedTerm) {
      return this.sections;
    }

    return this.sections
      .map((section) => ({
        ...section,
        shortcuts: section.shortcuts.filter((shortcut) =>
          String(this.appTranslateService.instant(shortcut.labelKey)).toLowerCase().includes(normalizedTerm)
        ),
      }))
      .filter((section) => section.shortcuts.length > 0);
  }

  protected getBindingKeys(action: ShortcutAction): string[] {
    return this.keyboardShortcutService.getBinding(action)?.split('+') ?? [];
  }

  public save() {
    // Nothing to save yet - shortcut customization UI is coming in a follow-up.
  }
}
